/**
 * Score calculation utilities for Mikkel Kviss
 */

/**
 * Calculate leaderboard from participants
 * @param {Array} participants - Array of participant objects with scores
 * @returns {Array} Sorted array of participants by score (highest first)
 */
export function calculateLeaderboard(participants) {
  if (!Array.isArray(participants)) {
    console.error('calculateLeaderboard: participants must be an array');
    return [];
  }

  // Create a copy to avoid mutating the original array
  const sortedParticipants = [...participants].sort((a, b) => {
    // Sort by score (descending)
    const scoreDiff = (b.score || 0) - (a.score || 0);

    // If scores are equal, sort by name alphabetically (ascending)
    if (scoreDiff === 0) {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    }

    return scoreDiff;
  });

  return sortedParticipants;
}

/**
 * Get participant's rank in the leaderboard
 * @param {Array} participants - Array of participant objects
 * @param {string} participantId - ID of the participant to find rank for
 * @returns {number} Rank (1-indexed), or -1 if participant not found
 */
export function getParticipantRank(participants, participantId) {
  if (!Array.isArray(participants)) {
    console.error('getParticipantRank: participants must be an array');
    return -1;
  }

  if (!participantId) {
    console.error('getParticipantRank: participantId is required');
    return -1;
  }

  // Calculate leaderboard to get sorted participants
  const leaderboard = calculateLeaderboard(participants);

  // Find the participant's position in the leaderboard
  const rank = leaderboard.findIndex(p => p.id === participantId);

  // Return 1-indexed rank, or -1 if not found
  return rank >= 0 ? rank + 1 : -1;
}

/**
 * Calculate points earned for an answer based on time elapsed
 * Uses scoring formula: faster answers get more points
 *
 * @param {boolean} isCorrect - Whether the answer was correct
 * @param {number} timeElapsed - Time taken to answer (in seconds)
 * @param {number} timeLimit - Total time allowed for the question (in seconds, default 30)
 * @returns {number} Points earned (0-1000)
 */
export function calculatePoints(isCorrect, timeElapsed, timeLimit = 30) {
  // Wrong answer = 0 points
  if (!isCorrect) {
    return 0;
  }

  // Ensure time values are valid
  const elapsed = Math.max(0, Math.min(timeElapsed, timeLimit));

  // Calculate time penalty: 0-500 points based on speed
  // Fastest answer (0s): 0 penalty = 1000 points
  // Slowest answer (30s): 500 penalty = 500 points
  const timePenalty = Math.floor((elapsed / timeLimit) * 500);

  // Calculate final points: 1000 - penalty, minimum 500 points
  const pointsEarned = Math.max(500, 1000 - timePenalty);

  return pointsEarned;
}
